<template>
<div>
    <CCol sm="12">
        <CCard>
            <CCardBody class="d-flex flex-column">
                <div class="d-flex mb-2 justify-content-end align-items-start">
                    <!-- Title -->
                    <p class="flex-grow-1" v-if="baseProduct && baseProduct.titles"><b>{{ $t('productIntro') }}</b> {{baseProduct.titles[currentLanguage.key]}}</p>
                    <!-- Language dropdown -->
                    <LanguageDropdown v-model="currentLanguage"></LanguageDropdown>
                </div>
                <!-- Form -->
                <div class="d-flex py-2 gap-1">
                    <!-- Featured -->
                    <div role="group" class="form-group">
                        <label> {{ $t('featured') }}</label>
                        <CInputCheckbox :checked='product.featured' @update:checked="product.featured = !product.featured" :invalid-feedback="errorOutput['Featured']" :is-valid="genericValidator(product.featured, attemptedSubmit) && errorValidator('Featured')"></CInputCheckbox>
                    </div>
                    <!-- Titles -->
                    <div role="group" class="form-group flex-grow-1">
                        <label class=""> {{ $t('titlesMandatory') }}</label>
                        <CInput class="m-0" type="text" v-model="product.titles[currentLanguage.key]" :invalid-feedback="errorOutput['Titles']" :is-valid="genericValidator(product.titles[currentLanguage.key], attemptedSubmit) && errorValidator('Titles')"></CInput>
                    </div>
                </div>
                <div class="row py-4">
                    <!-- featuredImage -->
                    <div role="group" class="form-group col-12 col-md-6">
                    <label class=""> {{ $t('featuredImageMandatory') }}</label>
                        <GalleryPicker v-model="product.featuredImage" :ratioWidth="1" :ratioHeight="1" v-bind:class="{ 'is-invalid': genericValidator(product.featuredImage, attemptedSubmit) === false || errorValidator('FeaturedImage') === false }" :max="1"></GalleryPicker>
                    </div>
                    <!-- images -->
                    <div role="group" class="form-group col-12 col-md-6">
                    <label class=""> {{ $t('images') }}</label>
                        <GalleryPicker v-model="product.images" :ratioWidth="1" :ratioHeight="1" v-bind:class="{ 'is-invalid': errorValidator('Images') === false }" :max="0"></GalleryPicker>
                    </div>
                </div>
                <div class="row">
                    <div class="d-flex flex-column col-12 col-lg-6">
                        <!-- tagLines -->
                        <div role="group" class="form-group">
                            <label class=""> {{ $t('tagLinesMandatory') }}</label>
                            <CInput class="m-0" type="text" v-model="product.tagLines[currentLanguage.key]" :invalid-feedback="errorOutput['TagLines']" :is-valid="genericValidator(product.tagLines[currentLanguage.key], attemptedSubmit) && errorValidator('TagLines')"></CInput>
                        </div>
                        <!-- Sku -->
                        <div role="group" class="form-group">
                            <label class=""> {{ $t('skuMandatory') }}</label>
                            <CInput class="m-0" type="text" v-model="product.sku" :invalid-feedback="errorOutput['Sku']" :is-valid="genericValidator(product.sku, attemptedSubmit) && errorValidator('Sku')"></CInput>
                        </div>
                        <!-- Price -->
                        <div role="group" class="form-group">
                        <label class=""> {{ $t('priceMandatory') }}</label>
                            <CInput type="number" class="m-0" v-model="product.price" :invalid-feedback="errorOutput['Price']" :is-valid="genericValidator(product.price, attemptedSubmit) && errorValidator('Price')"></CInput>
                        </div>
                        <!-- Tags -->
                        <div role="group" class="form-group">
                            <label class=""> {{ $t('tagsMandatory') }}</label>
                            <vue-tags-input v-model="tag" :tags="product.tags[currentLanguage.key]" @tags-changed="newTags => product.tags[currentLanguage.key] = newTags.map(x => x.text)"  v-bind:class="{'is-invalid': genericValidator(product.tags[currentLanguage.key], attemptedSubmit) == false || errorValidator('Tags') }" class="flex-grow-1"/>
                            <div v-if="genericValidator(product.tags[currentLanguage.key], attemptedSubmit) === false" class="invalid-feedback d-block">{{errorOutput['Tags']}}</div>
                        </div>
                    </div>
                    <div class="d-flex flex-column col-12 col-lg-6">
                        <!-- Categories -->
                        <div role="group" class="form-group">
                        <label class=""> {{ $t('categoriesMandatory') }}</label>
                            <OptionsDropdown v-model="product.categories" :options="categoriesOptions" :title="$t('categories')" :index="0" :is-valid="genericValidator(product.categories, attemptedSubmit) && errorValidator('Categories')" :invalid-feedback="errorOutput['Categories']"></OptionsDropdown>
                        </div>
                        <!-- publishDate -->
                        <div role="group" class="form-group">
                            <label class=""> {{ $t('publishDate') }}</label>
                            <datepicker class="form-control" v-model="product.publishDate" placeholder="DD-MM-YYYY" :disabled-dates="disabledDates"></datepicker>
                        </div>
                        <!-- Tax -->
                        <div role="group" class="form-group">
                            <label class=""> {{ $t('taxMandatory') }}</label>
                            <SingleOptionDropdown v-model="product.tax" :options="taxOptions" :title="$t('tax')" :index="0" :is-valid="genericValidator(product.tax, attemptedSubmit) && errorValidator('Tax')" :invalid-feedback="errorOutput['Tax']"></SingleOptionDropdown>
                        </div>
                        <!-- files (not required) -->
                        <div role="group" class="form-group">
                            <FilesPicker v-model="product.files" :title="$t('Files')" v-bind:class="{ 'is-invalid': errorValidator('Files') === false }" :invalidFeedback="errorOutput['Files']"></FilesPicker>
                        </div>
                    </div>
                </div>
                <!-- contents -->
                <div role="group" class="form-group">
                    <label class=""> {{ $t('contentsMandatory') }}</label>
                    <vue-editor v-model="product.contents[currentLanguage.key]" v-bind:class="{'is-invalid': genericValidator(product.contents[currentLanguage.key], attemptedSubmit) == false || errorValidator('Contents') }"></vue-editor>
                    <div v-if="errorOutput['Contents']" class="invalid-feedback">{{errorOutput['Contents']}}</div>
                </div>
                <!-- additionalInfos -->
                <div role="group" class="form-group">
                    <label class=""> {{ $t('additionalInfos') }}</label>
                    <vue-editor v-model="product.additionalInfos[currentLanguage.key]" v-bind:class="{'is-invalid': errorValidator('AdditionalInfos') }"></vue-editor>
                    <!-- <vue-editor v-model="product.additionalInfos[currentLanguage.key]" v-bind:class="{'is-invalid': genericValidator(product.additionalInfos[currentLanguage.key], attemptedSubmit) == false || errorValidator('AdditionalInfos') }"></vue-editor> -->
                    <div v-if="errorOutput['AdditionalInfos']" class="invalid-feedback">{{errorOutput['AdditionalInfos']}}</div>
                </div>
                <!-- CustomFields -->
                <div role="group" class="form-group m-0">
                    <label> {{ $t('customFieldsMandatory') }}</label>
                    <div class="d-flex align-items-center col-12 p-0 gap-2 flex-wrap">
                        <button type="button" class="btn btn-secondary" @click="addCurrentCustomField">{{ $t('addCustomField') }}</button>
                        <button type="button" class="btn btn-secondary ml-2" @click="removeCurrentCustomField">{{ $t('removeCustomField') }}</button>
                    </div>
                </div>
                <CTabs class="col-12 p-0 mt-3" v-if="product.customFields && product.customFields.length" variant="pills" :activeTab="currentCustomField">
                    <CTab v-for="(customField, index) in product.customFields" :key="index" :title="$t('CustomField') + ' ' + (index + 1)">
                        <div class="d-flex flex-column mt-2">
                            <div class="d-flex">
                                <div role="group" class="form-group">
                                    <label> {{ $t('required') }}</label>
                                    <CInputCheckbox :checked='customField.required' @update:checked="customField.required = !customField.required" :invalid-feedback="errorOutput['Required']" :is-valid="genericValidator(customField.required, attemptedSubmit) && errorValidator('Required')"></CInputCheckbox>
                                </div>
                                <!-- <div role="group" class="form-group ml-2" >
                                    <label> {{ $t('hasPrice') }}</label>
                                    <CInputCheckbox :checked='customField.hasPrice' v-bind:class="{ disabled : checkPrice && customField.hasPrice == undefined }" @update:checked="hasPriceUpdated(customField)"></CInputCheckbox>
                                </div>
                                <div role="group" class="form-group ml-1" >
                                    <Tooltip :text="$t('priceTooltip')"></Tooltip>
                                </div> -->
                            </div>
                            <!-- customFields.Names -->
                            <div role="group" class="form-group mt-4">
                                <label class=""> {{ $t('optionsNameMandatory') }}</label>
                                <CInput class="m-0" type="text" v-model="customField.names[currentLanguage.key]" :invalid-feedback="errorOutput.CustomFields[index].Names" :is-valid="genericValidator(customField.names, attemptedSubmit) && errorValidator(`CustomFields[${index}].Names`)"></CInput>
                            </div>
                        </div>
                        <!-- Options -->
                        <div role="group" class="form-group m-0">
                            <label> {{ $t('options') }}</label>
                            <div class="d-flex align-items-center col-12 p-0 gap-2 flex-wrap">
                                <button type="button" class="btn btn-secondary" @click="addCurrentOption(index)">{{ $t('addOption') }}</button>
                                <button type="button" class="btn btn-secondary ml-2" @click="removeCurrentOption(index)">{{ $t('removeOption') }}</button>
                            </div>
                            <CTabs class="col-12 p-0 mt-3" v-if="customField.options && customField.options.length" variant="pills" :activeTab="currentOption">
                                <CTab v-for="(option, indexO) in customField.options" :key="indexO" :title="$t('option') + ' ' + (indexO + 1)">
                                    <div class="d-flex flex-wrap">
                                        <!-- options.Names -->
                                        <div role="group" class="form-group mt-2 col-12 col-md-6">
                                            <label class=""> {{ $t('optionsNameMandatory') }}</label>
                                            <CInput class="m-0" type="text" v-model="option.names[currentLanguage.key]" :invalid-feedback="errorOutput.Options[index].Names" :is-valid="genericValidator(option.names, attemptedSubmit) && errorValidator(`Options[${index}].Names`)"></CInput>
                                        </div>
                                        <!-- options.price -->
                                        <!-- <div v-if="customField.hasPrice" role="group" class="form-group col-12 col-md-6">
                                            <label class=""> {{ $t('priceMandatory') }}</label>
                                            <CInput class="m-0 py-2" type="number" v-model="option.price" :is-valid="errorValidator(`Options[${index}].Price`)"></CInput>
                                        </div> -->
                                    </div>
                                </CTab>
                            </CTabs>
                        </div>
                    </CTab>
                </CTabs>
                <!-- Action button + submitting -->  
                <CSpinner v-if="submitting" variant="success" small class="align-self-end"></CSpinner>
                <CButton v-else type="button" class="btn btn-primary align-self-end" @click="submitClicked">{{ $t('acceptChanges') }}</CButton>
                <!-- Loading -->
                <FormLoading v-if="loading"></FormLoading>
            </CCardBody>
        </CCard>
    </CCol>
    <ConfirmationPopUp :confirmationMessage="confirmationMessage" @closeConfirmRequested="onClickConfirmationPopup" v-model="showConfirmationPopup"></ConfirmationPopUp>
</div>
</template>
 
<script>
import { mapState, mapActions } from 'vuex'
import router from '@/router/index';
import { API_HELPER } from "@/helpers/api.js";
import { deepClone } from "@/helpers/utils.js";
import { CONSTANTS } from '@/helpers/consts';
import { genericValidator, errorValidator } from "@/helpers/utils.js";
import InputGetList from "@/classes/InputGetList";
import CustomFields from "@/classes/CustomFields";
import Product from "@/classes/Product";
import CustomFieldOption from "@/classes/CustomFieldOption";
import LanguageDropdown from '@/controls/LanguageDropdown.vue'
import ConfirmationPopUp from "@/controls/popups/ConfirmationPopUp.vue";
import OptionsDropdown from '@/controls/OptionsDropdown.vue'
import SingleOptionDropdown from '@/controls/SingleOptionDropdown.vue'
import GalleryPicker from '@/controls/GalleryPicker.vue'
import FilesPicker from '@/controls/FilesPicker.vue'
import { VueEditor } from "vue2-editor";
import FormLoading from '@/controls/FormLoading.vue'
import Tooltip from '@/controls/Tooltip.vue'
import Datepicker from 'vuejs-datepicker';
import VueTagsInput from '@johmun/vue-tags-input';

  export default {
    components: { LanguageDropdown, ConfirmationPopUp, SingleOptionDropdown, VueEditor, GalleryPicker, OptionsDropdown, FormLoading, Datepicker, FilesPicker, VueTagsInput, Tooltip},
    props:{    
        baseProduct:
        {
            type: Object,
            default: null,
        },
        baseName:
        {
            type: String,
            default: null,
        },
        loading:
        {
            type: Boolean,
            default: false,
        },
    },
    data()
    {
        return {
            attemptedSubmit: false,
            currentLanguage: CONSTANTS.supportedLanguages[0],
            product: new Product({},{},{},{},{},[],[],[],[]),
            submitting: false,
            showConfirmationPopup: false,
            confirmationMessage:"",
            tag: '', 
            currentCustomField: 0,
            currentOption: 0,
            disabledDates: {
                to: new Date(new Date().setDate(new Date().getDate()-1))
            },
            taxOptions: [{label: this.$t("free"), key: "Free"}, {label: this.$t("reduced"), key: "Reduced"}, {label: this.$t("intermediate"), key: "Intermediate"}, {label: this.$t("normal "), key: "Normal"}]
        }
    },
    mounted: function()
    {
        this.resetProduct();
        this.getProductCategories();
    },
    computed:
    {
        ...mapState('products', ['putProductOutput']),
        ...mapState('products', ['postProductOutput']),
        ...mapState({ 
            alertType: state => state.alert.type,
            alertOutput: state => state.alert.output
        }),
        ...mapState('categories', ['getCategoriesOutput']),
        errorOutput: function()
        {
            var result = this.alertOutput && Array.isArray(this.alertOutput) ? this.alertOutput : [];

            // Validate multi language fields
            for (const [key, value] of Object.entries(this.product.titles)) { if(!value) { result['Titles'] = this.$i18n.t("validateBothTranslations"); break; }; }
            for (const [key, value] of Object.entries(this.product.tagLines)) { if(!value) { result['TagLines'] = this.$i18n.t("validateBothTranslations"); break; }; }
            for (const [key, value] of Object.entries(this.product.contents)) { if(!value) { result['Contents'] = this.$i18n.t("validateBothTranslations"); break; }; }
            // for (const [key, value] of Object.entries(this.product.additionalInfos)) { if(!value) { result['AdditionalInfos'] = this.$i18n.t("validateBothTranslations"); break; }; }
            for (const [key, value] of Object.entries(this.product.tags)) { if(!value || !value.length) { result['Tags'] = this.$i18n.t("validateBothTranslations"); break; }; }

            result.CustomFields = [];
            const self = this;

            this.product.customFields.forEach(function(customField){
                var error = {};

                for (const [key, value] of Object.entries(customField.names)) {
                    if(!value)
                    {
                        error['Names'] = self.$i18n.t("validateBothTranslations");
                        break;
                    }
                }
                result.CustomFields.push(error);
            });

            result.Options = [];
            this.product.customFields.forEach(function(customField){
                customField.options.forEach(function(option){
                    var error = {};

                    for (const [key, value] of Object.entries(option.names)) {
                        if(!value)
                        {
                            error['Names'] = self.$i18n.t("validateBothTranslations");
                            break;
                        }
                    }
                    result.Options.push(error);
                });
            });

            return result;
        },
        name: function () {
            return this.baseProduct || this.baseName ? (this.baseProduct ? this.baseProduct.name : this.baseName) : null;
        },
        categoriesInfo: function() {
            return this.getCategoriesOutput && this.getCategoriesOutput.data ? this.getCategoriesOutput.data : [];
        },
        categoriesOptions: function()
        {
            var result = [];

            if(this.categoriesInfo)
            {
                this.categoriesInfo.forEach(category => 
                {
                    result.push({key: category.id, label: category.name });
                });
            }

            return result;
        },
        checkPrice()
        {
            return this.product && this.product.customFields ? this.product.customFields.find((product) => product.hasPrice == true) : null;
        }
    },
    methods:{
        genericValidator,
        errorValidator,
        ...mapActions('categories', ['getCategories']),
        resetProduct(){
            if(!this.baseProduct)
            {
                var titles = {}
                var tagLines = {}
                var contents = {}
                var additionalInfos = {}
                var tags = {}
                
                CONSTANTS.supportedLanguages.forEach(language => { titles[language.key]= "", tagLines[language.key]= "", contents[language.key]= "", additionalInfos[language.key]= "", tags[language.key] = []});
                this.product = new Product(titles,tagLines,contents,additionalInfos,tags,[],[],[],[]);
            }
            else
            {
                // hasPrice
                // this.baseProduct.customFields.forEach((customField) => {
                //     customField.hasPrice = customField.options.some((option) => option.price);
                // })

                this.product = deepClone(new Product, this.baseProduct);   

                // featuredImage
                this.product.featuredImage = {url: API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, this.product.featuredImagePath]), path: this.product.featuredImagePath};

                // Images
                var images = [];
                this.product.imagesPath.forEach(function(file)
                {
                    images.push({url: API_HELPER.pathJoin([process.env.VUE_APP_URL_ROOT, file]), path: file});
                });
                this.product.images = images;

                // Files
                var files = [];
                this.product.filesPath.forEach(function(file)
                {
                    files.push({path: file});
                });
                this.product.files = files;

                //categories
                this.product.categories = this.product.categories.map(category => category.id)
            }
        },
        submitClicked(){
            this.attemptedSubmit = true;
            
            // Emit event
            if(this.product.isValid())
            {
                this.submitting = true;
                this.$emit('submit-requested', this.product);
            }
        },
        onClickConfirmationPopup(value){
            this.showConfirmationPopup = value;     
            router.push({name: 'Products'});
        },
        onProcessTask: function(val)
        {
            if(val != null)
            {
                if(val)
                {
                    this.showConfirmationPopup = true;
                }

                this.submitting = false;
            }
        },
        getProductCategories(){
            const itemsPerPage = 999;
            const currentPage = 1;
            const searchText = null;
            const sorter = 1; // ASC
            const propertyName = "name";

            var input = new InputGetList(itemsPerPage, currentPage, searchText, sorter, propertyName);
            input.type = 4; // type -> Product

            this.getCategories(input);         
        },
        // CustomFields
        addCurrentCustomField()
        {
            var names = {};

            CONSTANTS.supportedLanguages.forEach(language => { names[language.key] = "" });
   
            this.product.customFields.push(new CustomFields(names, []));
        },
        removeCurrentCustomField()
        {
            this.product.customFields.splice(this.currentCustomField, 1);
        },
        // Options
        addCurrentOption(index)
        {
            var names = {};

            CONSTANTS.supportedLanguages.forEach(language => { names[language.key] = "" });

            this.product.customFields[index].options.push(new CustomFieldOption(names));
            // this.product.customFields[index].options.push(new CustomFieldOption(names, 0));
        },
        removeCurrentOption(index)
        {
            this.product.customFields[index].options.splice(this.currentOption, 1);
        },
        hasPriceUpdated(customField)
        {
            this.product.customFields.forEach(customField => {
                customField.options.forEach(option => {
                    delete option.price;
                })
            })

            customField.hasPrice = !customField.hasPrice;
        }
    },
    watch:
    {
        product:
        {
            handler(val)
            {
                val.customFields.forEach((product) => {
                    if(product.hasPrice == false)
                    {
                        product.hasPrice = undefined
                    }
                })
            },
            deep: true
        },
        putProductOutput: function(val)
        {
            this.onProcessTask(val);
        },
        postProductOutput: function(val)
        {
            this.onProcessTask(val);
        },
        baseProduct: function()
        {
            this.resetProduct();
        },
        alertOutput: function()
        {
            // Show pop up in case it is a string error
            // In case it is an array, it is shown on the field
            if(typeof this.alertOutput === 'string' || this.alertOutput instanceof String)
            {
                alert(this.alertOutput);
            }
        }
    }
  }
</script> 
 
<style scoped>

@media only screen and (max-width: 767px) {
    .main-content {
        flex-direction: column;
    }
}

/deep/.quillWrapper.is-invalid .ql-snow.ql-toolbar,
/deep/.quillWrapper.is-invalid .ql-snow.ql-container
{
    border-color: #e55353;
}
>>>.vue-tags-input.is-invalid{
    border: 1px solid #e55353;
}
>>>.vue-tags-input{
    border: 1px solid #ccc;
    max-width: unset;
}
>>>.vue-tags-input {
  max-width: unset;
}
>>>.ti-input{
    border: none;
}

>>>.vdp-datepicker input{
    border: none!important;
}
</style> 